import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Calculator, Lightbulb, Cable, Zap, Server, ToggleRight, Package,
  Hammer, X, Loader2, Plus, Search, Check, Ruler, ArrowLeft, RotateCcw
} from "lucide-react";
import axios from "axios";
import { useCatalogoPresupuesto } from "./catalogoScope";
import { toast } from "sonner";
import { fmt, opts, seleccionarAlEntrar } from "./constants";   // Fix IP: la celda de precio, como en una planilla

// ─── Eval seguro para expresiones matemáticas (×, ÷, +, -, paréntesis) ───
// Permite: dígitos, + - * / × ÷ x , . ( ) y espacios. Reemplaza × por *, ÷ por /.
const SAFE_MATH_REGEX = /^[0-9+\-*/.() ×÷xX]+$/;
function safeEvalMath(expr) {
  if (!expr || typeof expr !== "string") return null;
  const cleaned = expr.replace(/×/g, "*").replace(/÷/g, "/").replace(/[xX]/g, "*").replace(/,/g, ".").trim();
  if (!cleaned) return null;
  if (!SAFE_MATH_REGEX.test(expr)) return null;
  // Si es solo un número, lo devolvemos tal cual
  if (/^-?\d+(\.\d+)?$/.test(cleaned)) return parseFloat(cleaned);
  try {
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict"; return (${cleaned});`)();
    return Number.isFinite(result) ? result : null;
  } catch { return null; }
}

// Catálogos disponibles para C.Unit MAT
const CATALOGOS_MAT = [
  { key: "bocas",             label: "Bocas",          icon: Lightbulb,   color: "text-amber-600",  endpoint: "/bocas-precios/bocas",      mode: "boca" },
  { key: "cables",            label: "Cables",         icon: Cable,       color: "text-orange-600", endpoint: "/catalogos/cables",         mode: "cable" },
  { key: "disyuntores",       label: "Disyuntores",    icon: Zap,         color: "text-yellow-600", endpoint: "/catalogos/disyuntores",    mode: "generic" },
  { key: "tableros",          label: "Tableros",       icon: Server,      color: "text-blue-600",   endpoint: "/catalogos/tableros",       mode: "generic" },
  { key: "mecanismos",        label: "Mecanismos",     icon: ToggleRight, color: "text-violet-600", endpoint: "/catalogos/mecanismos",     mode: "mecanismo" },
  { key: "materiales_varios", label: "Mat. Varios",    icon: Package,     color: "text-gray-600",   endpoint: "/catalogos/materiales_varios", mode: "generic" },
];

// Catálogo único para C.Unit M.O
const CATALOGO_MO = { key: "mo", label: "Mano de Obra", icon: Hammer, color: "text-emerald-600", endpoint: "/bocas-precios/mano-obra", mode: "mo" };

// Fix AJ (2026-09-04): "al elegir la boca (…) se debe SOLICITAR el metraje a
// imputar a la boca (…) cada obra tiene un aspecto constructivo distinto y eso
// incide directamente en el metraje" (Edson, 04/09). Unidades que se marcan como
// metros para resaltarlas en el paso de metraje.
const esMetro = (u) => /^(mts|ml|m|mt|metro|metros)$/i.test(String(u || "").trim());
const claveComp = (d, u) => `${String(d || "").trim().toLowerCase()}|${String(u || "un").trim().toLowerCase()}`;
const numDe = (v) => {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

// Paso de metraje: muestra la composición de la boca elegida con la cantidad de
// cada componente editable (las de metros resaltadas), recalcula el precio en
// vivo y devuelve el precio nuevo + el metraje usado, que se guarda EN EL ÍTEM
// (el catálogo no se toca).
function BocaMetrajeStep({ boca, metrajeGuardado, onCancel, onConfirm }) {
  const comps = boca.componentes || [];
  const [cant, setCant] = useState(() => {
    const guardadas = {};
    const previo = metrajeGuardado && metrajeGuardado.boca_id === boca._id ? (metrajeGuardado.componentes || []) : [];
    previo.forEach(c => { guardadas[claveComp(c.descripcion, c.unidad)] = c.cantidad; });
    const out = {};
    comps.forEach((c, i) => {
      const g = guardadas[claveComp(c.descripcion, c.unidad)];
      out[i] = String(g != null ? g : (Number(c.cantidad) || 0));
    });
    return out;
  });

  const total = comps.reduce((s, c, i) => s + numDe(cant[i]) * (Number(c.precio_unit) || 0), 0);
  const catalogo = Number(boca.precio_mat) || 0;
  const dif = Math.round((total - catalogo) * 100) / 100;
  const hayMetros = comps.some(c => esMetro(c.unidad));

  const volverAlCatalogo = () => {
    const out = {};
    comps.forEach((c, i) => { out[i] = String(Number(c.cantidad) || 0); });
    setCant(out);
  };

  const confirmar = () => {
    onConfirm(Math.round(total * 100) / 100, comps.map((c, i) => ({
      descripcion: c.descripcion || "", unidad: c.unidad || "un", cantidad: numDe(cant[i]),
    })));
  };

  return (
    <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="boca-metraje-step">
      <div className="flex items-center gap-2 mb-3">
        <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" data-testid="boca-metraje-volver" title="Volver al catálogo">
          <ArrowLeft size={15} />
        </button>
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900">{boca.tipo_boca}</p>
          <p className="text-[11px] text-gray-500">{boca.descripcion}</p>
        </div>
      </div>
      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
        <Ruler size={12} className="inline mr-1 -mt-0.5" />
        {hayMetros
          ? "Cargá el metraje real de esta obra: los caños y cables cambian según cómo esté construido el edificio. El precio de la boca se recalcula con lo que pongas, y el consumo de la obra se mide contra estas cantidades."
          : "Ajustá las cantidades de esta obra si hacen falta. El precio de la boca se recalcula con lo que pongas, y el consumo de la obra se mide contra estas cantidades."}
      </p>
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-[#EBF3FB]">
            <tr>
              <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Componente</th>
              <th className="text-center px-2 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide w-16">Un.</th>
              <th className="text-right px-2 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide w-24">Cantidad</th>
              <th className="text-right px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide w-28">Precio u.</th>
              <th className="text-right px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide w-28">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {comps.map((c, i) => {
              const metro = esMetro(c.unidad);
              const catalogoCant = Number(c.cantidad) || 0;
              const cambiada = numDe(cant[i]) !== catalogoCant;
              return (
                <tr key={i} className={`border-t border-gray-100 ${metro ? "bg-amber-50/40" : ""}`} data-testid={`boca-metraje-row-${i}`}>
                  <td className="px-3 py-1.5 text-gray-800 whitespace-normal break-words">
                    {c.descripcion}
                    {cambiada && <span className="ml-1 text-[10px] text-amber-700">(catálogo: {catalogoCant})</span>}
                  </td>
                  <td className={`px-2 py-1.5 text-center ${metro ? "text-amber-700 font-bold" : "text-gray-500"}`}>{c.unidad}</td>
                  <td className="px-2 py-1.5 text-right">
                    <input value={cant[i]} onChange={e => setCant(p => ({ ...p, [i]: e.target.value }))}
                      inputMode="decimal"
                      className={`w-20 border rounded px-1 py-0.5 text-xs text-right ${cambiada ? "border-amber-400 bg-amber-50" : "border-gray-200"}`}
                      data-testid={`boca-metraje-cant-${i}`} />
                  </td>
                  <td className="px-3 py-1.5 text-right text-gray-500 tabular-nums">{fmt(Number(c.precio_unit) || 0)}</td>
                  <td className="px-3 py-1.5 text-right font-semibold text-blue-700 tabular-nums">{fmt(Math.round(numDe(cant[i]) * (Number(c.precio_unit) || 0) * 100) / 100)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
        <button onClick={volverAlCatalogo} className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 hover:text-gray-700" data-testid="boca-metraje-reset">
          <RotateCcw size={12} /> Volver a las cantidades del catálogo
        </button>
        <div className="text-right">
          <p className="text-[11px] text-gray-500">Catálogo: {fmt(catalogo)}{dif !== 0 ? ` · diferencia ${dif > 0 ? "+" : ""}${fmt(dif)}` : ""}</p>
          <p className="text-lg font-bold text-[#1A4B7A] tabular-nums" data-testid="boca-metraje-total">{fmt(Math.round(total * 100) / 100)}</p>
        </div>
      </div>
      <div className="flex gap-2 justify-end mt-3">
        <button onClick={onCancel} className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600">Cancelar</button>
        <button onClick={confirmar} className="flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2 rounded-lg text-xs font-semibold" data-testid="boca-metraje-usar">
          <Check size={13} /> Usar este precio
        </button>
      </div>
    </div>
  );
}

// ─── Componente principal — celda de precio ───────────────────────────────
export function PriceCellInput({ value, onChange, type = "mat", testId, placeholder = "0", combinatorMecanismos = null, combinatorCables = null, onCombinatorChange = null, bocaMetraje = null, onBocaSelect = null }) {
  const [editing, setEditing] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const inputRef = useRef(null);

  // Sync `editing` con `value` externo
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setEditing(value != null && value !== 0 ? String(value) : "");
    }
  }, [value]);

  const handleInputChange = (e) => {
    setEditing(e.target.value);
  };

  const handleBlur = () => {
    const result = safeEvalMath(editing);
    if (result != null) {
      const rounded = Math.round(result * 100) / 100;
      onChange(rounded);
      setEditing(rounded ? String(rounded) : "");
    } else if (editing.trim() === "") {
      onChange(0);
    }
    // si no parsea, dejamos como estaba
  };

  const handleKey = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    } else if (e.key === "Escape") {
      setEditing(value ? String(value) : "");
      e.target.blur();
    }
  };

  const handlePickerSelect = (price) => {
    const rounded = Math.round((Number(price) || 0) * 100) / 100;
    onChange(rounded);
    setEditing(rounded ? String(rounded) : "");
    setPickerOpen(false);
    toast.success(`Precio cargado: ${fmt(rounded)}`);
  };

  // Fix AJ (2026-09-04): boca elegida con su metraje de obra. Además del precio,
  // guarda en el ítem el vínculo a la boca (`catalogo_ref`) — sin él, el listado
  // desglosado no explota el ítem y el consumo de la obra nunca ve el caño ni los
  // cables — y el metraje usado.
  const handleBocaSelect = (precio, boca, componentes) => {
    const rounded = Math.round((Number(precio) || 0) * 100) / 100;
    onChange(rounded);
    setEditing(rounded ? String(rounded) : "");
    onBocaSelect?.(rounded,
      { id: `boca:${boca._id}`, tipo: "boca", descripcion: boca.tipo_boca || boca.descripcion || "" },
      { boca_id: boca._id, boca_tipo: boca.tipo_boca || "", componentes });
    setPickerOpen(false);
    toast.success(`Precio de la boca: ${fmt(rounded)}`);
  };

  // FIX 1 — "Cargar Total" de la calculadora: carga el total Y persiste la combinación.
  const handleCombinationSelect = (total, kind, savedRows) => {
    const rounded = Math.round((Number(total) || 0) * 100) / 100;
    onChange(rounded);
    setEditing(rounded ? String(rounded) : "");
    if (typeof onCombinatorChange === "function") {
      if (kind === "mecanismo") onCombinatorChange("combinator_mecanismos", savedRows);
      else if (kind === "cable") onCombinatorChange("combinator_cables", savedRows);
    }
    setPickerOpen(false);
    toast.success(`Total cargado: ${fmt(rounded)}`);
  };

  const isMath = editing && /[+\-*/×÷xX]/.test(editing);

  return (
    <div className="relative flex items-center gap-0.5">
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={editing}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKey}
        {...seleccionarAlEntrar}
        placeholder={placeholder}
        title={isMath ? `Expresión: ${editing} = ${safeEvalMath(editing) ?? "?"}` : undefined}
        className={`w-full border rounded px-1 py-0.5 text-xs text-right focus:ring-1 focus:ring-[#2E6DA4] focus:border-transparent ${isMath ? "border-amber-300 bg-amber-50 font-mono" : "border-gray-200"}`}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        className="flex-shrink-0 p-0.5 text-gray-400 hover:text-[#2E6DA4] transition-colors"
        title="Cargar desde catálogo"
        data-testid={`${testId}-picker-btn`}
      >
        <Calculator size={11} />
      </button>

      <PricePickerModal
        open={pickerOpen}
        type={type}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
        onSelectCombination={handleCombinationSelect}
        onSelectBoca={handleBocaSelect}
        bocaMetraje={bocaMetraje}
        combinatorMecanismos={combinatorMecanismos}
        combinatorCables={combinatorCables}
      />
    </div>
  );
}

// ─── Modal Picker — jerárquico por catálogo ──────────────────────────────
function PricePickerModal({ open, type, onClose, onSelect, onSelectCombination, onSelectBoca, bocaMetraje, combinatorMecanismos, combinatorCables }) {
  // Fix CC (2026-09-10): los precios salen de la copia de ESTE presupuesto (en uno
  // nuevo sin guardar, de la base madre, que es lo que se le va a copiar).
  const { catApi } = useCatalogoPresupuesto();
  const catalogos = type === "mo" ? [CATALOGO_MO] : CATALOGOS_MAT;
  const [activeCat, setActiveCat] = useState(catalogos[0].key);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterMarca, setFilterMarca] = useState("todas");
  // Fix AJ (2026-09-04): boca elegida a la que se le está cargando el metraje.
  const [bocaElegida, setBocaElegida] = useState(null);

  const activeCatalog = catalogos.find(c => c.key === activeCat) || catalogos[0];

  const fetchItems = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${catApi}${activeCatalog.endpoint}`, opts);
      setItems(Array.isArray(data) ? data : []);
    } catch { toast.error("Error al cargar catálogo"); setItems([]); }
    finally { setLoading(false); }
  }, [open, activeCatalog.endpoint, catApi]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Reset search when changing tab
  useEffect(() => { setSearch(""); setFilterMarca("todas"); setBocaElegida(null); }, [activeCat]);
  useEffect(() => { if (!open) setBocaElegida(null); }, [open]);

  if (!open) return null;

  const marcasUnicas = activeCatalog.mode === "generic" || activeCatalog.mode === "cable" || activeCatalog.mode === "mecanismo"
    ? [...new Set(items.map(i => (i.marca || "").trim()).filter(Boolean))].sort()
    : [];

  const filtered = items.filter(it => {
    const matchSearch = !search ||
      (it.descripcion || "").toLowerCase().includes(search.toLowerCase()) ||
      (it.tipo_boca || "").toLowerCase().includes(search.toLowerCase()) ||
      (it.modelo || "").toLowerCase().includes(search.toLowerCase()) ||
      (it.marca || "").toLowerCase().includes(search.toLowerCase());
    const matchMarca = filterMarca === "todas" || (it.marca || "").trim() === filterMarca;
    return matchSearch && matchMarca;
  });

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="price-picker-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Calculator size={18} className="text-[#2E6DA4]" />
              Cargar precio desde catálogo
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {type === "mo" ? "Seleccioná un rubro de Mano de Obra" : "Seleccioná un material del catálogo unificado"}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>

        {/* Sub-tabs catálogos */}
        {catalogos.length > 1 && (
          <div className="flex gap-1 border-b border-gray-100 px-6 overflow-x-auto flex-shrink-0">
            {catalogos.map(c => {
              const Icon = c.icon;
              const isA = activeCat === c.key;
              return (
                <button key={c.key} onClick={() => setActiveCat(c.key)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold border-b-2 transition-all whitespace-nowrap ${
                    isA ? "border-[#2E6DA4] text-[#2E6DA4]" : "border-transparent text-gray-400 hover:text-gray-600"
                  }`}
                  data-testid={`picker-cat-${c.key}`}>
                  <Icon size={12} className={c.color} /> {c.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Toolbar (search + filter marca) */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-50 flex-shrink-0 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="picker-search-input" autoFocus />
          </div>
          {marcasUnicas.length > 1 && (
            <select value={filterMarca} onChange={e => setFilterMarca(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="picker-marca-filter">
              <option value="todas">Todas las marcas</option>
              {marcasUnicas.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
          <span className="text-xs text-gray-500 ml-auto">{filtered.length} ítem{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Cable Combination Builder */}
        {activeCatalog.mode === "cable" && filtered.length > 0 && (
          <CableCombinationBuilder
            cables={filtered}
            onSelect={onSelect}
            initialCombination={combinatorCables}
            onConfirmCombination={(total, rows) => onSelectCombination && onSelectCombination(total, "cable", rows)}
          />
        )}

        {/* C.1 — Mecanismos Combination Builder (mismo patrón que cables) */}
        {activeCatalog.mode === "mecanismo" && filtered.length > 0 && (
          <MecanismosCombinationBuilder
            mecanismos={filtered}
            onSelect={onSelect}
            initialCombination={combinatorMecanismos}
            onConfirmCombination={(total, rows) => onSelectCombination && onSelectCombination(total, "mecanismo", rows)}
          />
        )}

        {/* Fix AJ (2026-09-04): paso de metraje antes de cargar el precio de la boca. */}
        {bocaElegida ? (
          <BocaMetrajeStep
            boca={bocaElegida}
            metrajeGuardado={bocaMetraje}
            onCancel={() => setBocaElegida(null)}
            onConfirm={(precio, componentes) => { setBocaElegida(null); onSelectBoca?.(precio, bocaElegida, componentes); }}
          />
        ) : (
        /* Lista */
        <div className="flex-1 overflow-y-auto px-6 py-3">
          {loading ? (
            <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Package size={32} className="mx-auto mb-2 opacity-30" />
              <p className="font-medium">No hay ítems en este catálogo</p>
              <p className="text-xs mt-1">Cargá precios desde el tab "{type === "mo" ? "Mano de Obra" : "Catálogos"}".</p>
            </div>
          ) : activeCatalog.mode === "cable" || activeCatalog.mode === "mecanismo" ? null : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filtered.map(it => (
                <PickerRow key={it._id} item={it} mode={activeCatalog.mode} onSelect={() => {
                  // Fix AJ (2026-09-04): una boca CON composición pasa primero por el
                  // metraje. Sin composición no hay nada que medir: se carga el precio
                  // como siempre.
                  if (activeCatalog.mode === "boca" && (it.componentes || []).length > 0 && onSelectBoca) {
                    setBocaElegida(it);
                    return;
                  }
                  const price = activeCatalog.mode === "mo" ? it.precio_unitario : (it.precio_mat ?? it.precio_unitario);
                  onSelect(price);
                }} />
              ))}
            </div>
          )}
        </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function PickerRow({ item, mode, onSelect }) {
  const title = mode === "boca" ? item.tipo_boca
    : mode === "mo" ? item.descripcion
    : `${item.marca || "—"} · ${item.modelo || "—"}`;
  const subtitle = mode === "boca" ? item.descripcion
    : mode === "mo" ? item.categoria
    : item.descripcion;
  const price = mode === "mo" ? item.precio_unitario
    : mode === "boca" ? item.precio_mat
    : item.precio_unitario;
  const meta = item.meta || {};
  const metaLine = [meta.calibre, meta.tipo, meta.polos ? `${meta.polos}P` : null, meta.amperaje ? `${meta.amperaje}A` : null]
    .filter(Boolean).join(" · ");

  return (
    <button onClick={onSelect}
      className="text-left bg-gray-50 hover:bg-[#EBF3FB] border border-gray-100 hover:border-[#2E6DA4] rounded-xl px-3 py-2.5 transition-colors group"
      data-testid={`picker-item-${item._id}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="text-xs font-semibold text-gray-900 truncate">{title}</p>
        <Check size={14} className="text-[#2E6DA4] opacity-0 group-hover:opacity-100 flex-shrink-0" />
      </div>
      {subtitle && <p className="text-[10px] text-gray-500 truncate">{subtitle}</p>}
      {metaLine && <p className="text-[9px] text-gray-400 mt-0.5 italic">{metaLine}</p>}
      <p className="text-sm font-bold text-blue-700 mt-1 tabular-nums">{fmt(price)} <span className="text-[9px] font-normal text-gray-500">/{item.unidad || "un"}</span></p>
    </button>
  );
}

// ─── CABLES — combination builder con multiplicadores 1-10 (PDF p.10) ─────
function CableCombinationBuilder({ cables, onSelect, initialCombination = null, onConfirmCombination = null }) {
  // Combinación: lista de { cable_id, qty (1-10) }. FIX 1: pre-carga desde la memoria del item.
  const [rows, setRows] = useState(() => {
    const init = Array.isArray(initialCombination) ? initialCombination : [];
    return init.length > 0
      ? init.map(c => ({ cable_id: c.cable_id || "", qty: Number(c.mult) || 1 }))
      : [{ cable_id: "", qty: 1 }];
  });

  const addRow = () => setRows(r => [...r, { cable_id: "", qty: 1 }]);
  const removeRow = (idx) => setRows(r => r.filter((_, i) => i !== idx));
  const updateRow = (idx, key, val) => setRows(r => r.map((row, i) => i === idx ? { ...row, [key]: val } : row));

  const total = rows.reduce((sum, r) => {
    const c = cables.find(x => x._id === r.cable_id);
    if (!c) return sum;
    return sum + (Number(c.precio_unitario) || 0) * (Number(r.qty) || 0);
  }, 0);

  const formula = rows
    .filter(r => r.cable_id && Number(r.qty) > 0)
    .map(r => {
      const c = cables.find(x => x._id === r.cable_id);
      if (!c) return "";
      return `${r.qty}×${c.meta?.calibre || c.modelo || c.descripcion}`;
    })
    .join(" + ");

  // FIX 1 — confirma cargando el total Y persistiendo la combinación en formato guardable.
  const confirm = () => {
    const saved = rows
      .filter(r => r.cable_id && Number(r.qty) > 0)
      .map(r => {
        const c = cables.find(x => x._id === r.cable_id);
        return { mult: Number(r.qty), cable_id: r.cable_id, p_unit: Number(c?.precio_unitario) || 0 };
      });
    if (onConfirmCombination) onConfirmCombination(total, saved);
    else onSelect(total);
  };

  return (
    <div className="border-t border-b border-amber-100 bg-amber-50/40 px-6 py-3 space-y-2 flex-shrink-0" data-testid="cable-combination-builder">
      <p className="text-xs font-bold text-amber-900 uppercase tracking-wide flex items-center gap-1.5">
        <Cable size={12} /> Calculadora de combinación de cables
        <span className="text-[10px] font-normal text-amber-700 normal-case ml-1">(MULT × P.UNIT) + (MULT × P.UNIT) + ...</span>
      </p>
      {rows.map((row, idx) => {
        const cable = cables.find(c => c._id === row.cable_id);
        const lineTotal = cable ? (Number(cable.precio_unitario) || 0) * (Number(row.qty) || 0) : 0;
        return (
          <div key={idx} className="flex items-center gap-2 text-xs" data-testid={`cable-row-${idx}`}>
            <select value={row.qty} onChange={e => updateRow(idx, "qty", parseInt(e.target.value))}
              className="border border-gray-200 rounded px-2 py-1 bg-white w-16"
              data-testid={`cable-qty-${idx}`}>
              {Array.from({ length: 10 }, (_, i) => i + 1).map(n => <option key={n} value={n}>×{n}</option>)}
            </select>
            <select value={row.cable_id} onChange={e => updateRow(idx, "cable_id", e.target.value)}
              className="flex-1 border border-gray-200 rounded px-2 py-1 bg-white"
              data-testid={`cable-select-${idx}`}>
              <option value="">— Seleccionar cable —</option>
              {cables.map(c => (
                <option key={c._id} value={c._id}>
                  {c.marca} · {c.meta?.calibre || c.modelo || ""} · {fmt(c.precio_unitario)}/{c.unidad}
                </option>
              ))}
            </select>
            <span className="font-bold text-blue-700 tabular-nums w-24 text-right">{fmt(lineTotal)}</span>
            {rows.length > 1 && (
              <button onClick={() => removeRow(idx)} className="text-red-500 hover:bg-red-50 p-1 rounded">
                <X size={12} />
              </button>
            )}
          </div>
        );
      })}
      <div className="flex items-center gap-2 pt-1.5 border-t border-amber-200">
        <button onClick={addRow}
          className="flex items-center gap-1 text-[10px] font-semibold text-amber-700 hover:bg-amber-100 px-2 py-1 rounded"
          data-testid="cable-add-row-btn">
          <Plus size={10} /> Agregar otro cable
        </button>
        <p className="text-[10px] text-gray-600 italic">{formula || "—"}</p>
        <button onClick={confirm} disabled={total === 0}
          className="ml-auto flex items-center gap-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white text-xs font-bold px-3 py-1.5 rounded-lg disabled:opacity-40"
          data-testid="cable-confirm-combination-btn">
          <Check size={12} /> Cargar Total: {fmt(total)}
        </button>
      </div>
    </div>
  );
}


// ─── MECANISMOS — combination builder (C.1, PDF REV5 pág 5) ───────────────
// Mismo patrón que CableCombinationBuilder: multiplicador 1-10 × precio_unitario
// del catálogo de Mecanismos. "Cargar Total" pega el resultado en la celda
// C.Unit MAT del ítem que el usuario está editando.
function MecanismosCombinationBuilder({ mecanismos, onSelect, initialCombination = null, onConfirmCombination = null }) {
  // FIX 1: pre-carga desde la memoria del item (combinator_mecanismos).
  const [rows, setRows] = useState(() => {
    const init = Array.isArray(initialCombination) ? initialCombination : [];
    return init.length > 0
      ? init.map(c => ({ mec_id: c.mecanismo_id || "", qty: Number(c.mult) || 1 }))
      : [{ mec_id: "", qty: 1 }];
  });

  const addRow = () => setRows(r => [...r, { mec_id: "", qty: 1 }]);
  const removeRow = (idx) => setRows(r => r.filter((_, i) => i !== idx));
  const updateRow = (idx, key, val) => setRows(r => r.map((row, i) => i === idx ? { ...row, [key]: val } : row));

  const total = rows.reduce((sum, r) => {
    const m = mecanismos.find(x => x._id === r.mec_id);
    if (!m) return sum;
    return sum + (Number(m.precio_unitario) || 0) * (Number(r.qty) || 0);
  }, 0);

  const formula = rows
    .filter(r => r.mec_id && Number(r.qty) > 0)
    .map(r => {
      const m = mecanismos.find(x => x._id === r.mec_id);
      if (!m) return "";
      return `${r.qty}×${m.modelo || m.descripcion || m.marca || "mec"}`;
    })
    .join(" + ");

  // FIX 1 — confirma cargando el total Y persistiendo la combinación en formato guardable.
  const confirm = () => {
    const saved = rows
      .filter(r => r.mec_id && Number(r.qty) > 0)
      .map(r => {
        const m = mecanismos.find(x => x._id === r.mec_id);
        return { mult: Number(r.qty), mecanismo_id: r.mec_id, p_unit: Number(m?.precio_unitario) || 0 };
      });
    if (onConfirmCombination) onConfirmCombination(total, saved);
    else onSelect(total);
  };

  return (
    <div className="border-t border-b border-violet-100 bg-violet-50/40 px-6 py-3 space-y-2 flex-shrink-0" data-testid="mecanismos-combinator">
      <p className="text-xs font-bold text-violet-900 uppercase tracking-wide flex items-center gap-1.5">
        <ToggleRight size={12} /> Calculadora de combinación de mecanismos
        <span className="text-[10px] font-normal text-violet-700 normal-case ml-1">(MULT × P.UNIT) + (MULT × P.UNIT) + ...</span>
      </p>
      {rows.map((row, idx) => {
        const mec = mecanismos.find(c => c._id === row.mec_id);
        const lineTotal = mec ? (Number(mec.precio_unitario) || 0) * (Number(row.qty) || 0) : 0;
        return (
          <div key={idx} className="flex items-center gap-2 text-xs" data-testid={`mecanismos-row-${idx}`}>
            <select value={row.qty} onChange={e => updateRow(idx, "qty", parseInt(e.target.value))}
              className="border border-gray-200 rounded px-2 py-1 bg-white w-16"
              data-testid={`mecanismos-qty-${idx}`}>
              {Array.from({ length: 10 }, (_, i) => i + 1).map(n => <option key={n} value={n}>×{n}</option>)}
            </select>
            <select value={row.mec_id} onChange={e => updateRow(idx, "mec_id", e.target.value)}
              className="flex-1 border border-gray-200 rounded px-2 py-1 bg-white"
              data-testid={`mecanismos-select-${idx}`}>
              <option value="">— Seleccionar mecanismo —</option>
              {mecanismos.map(m => (
                <option key={m._id} value={m._id}>
                  {m.marca || "—"} · {m.modelo || m.descripcion || ""} · {fmt(m.precio_unitario)}/{m.unidad || "un"}
                </option>
              ))}
            </select>
            <span className="font-bold text-blue-700 tabular-nums w-24 text-right">{fmt(lineTotal)}</span>
            {rows.length > 1 && (
              <button onClick={() => removeRow(idx)} className="text-red-500 hover:bg-red-50 p-1 rounded"
                data-testid={`mecanismos-remove-${idx}`}>
                <X size={12} />
              </button>
            )}
          </div>
        );
      })}
      <div className="flex items-center gap-2 pt-1.5 border-t border-violet-200">
        <button onClick={addRow}
          className="flex items-center gap-1 text-[10px] font-semibold text-violet-700 hover:bg-violet-100 px-2 py-1 rounded"
          data-testid="mecanismos-add-row-btn">
          <Plus size={10} /> Agregar otro mecanismo
        </button>
        <p className="text-[10px] text-gray-600 italic">{formula || "—"}</p>
        <button onClick={confirm} disabled={total === 0}
          className="ml-auto flex items-center gap-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white text-xs font-bold px-3 py-1.5 rounded-lg disabled:opacity-40"
          data-testid="mecanismos-confirm-combination-btn">
          <Check size={12} /> Cargar Total: {fmt(total)}
        </button>
      </div>
    </div>
  );
}
